import { isNativeShell, nativeLocation, nativeLocationPermission } from './native'

export type GeoErrorKind = 'unsupported' | 'insecure' | 'denied' | 'unavailable' | 'timeout'

export class GeoError extends Error {
  readonly kind: GeoErrorKind

  constructor(kind: GeoErrorKind, message: string) {
    super(message)
    this.name = 'GeoError'
    this.kind = kind
  }
}

const MESSAGES: Record<GeoErrorKind, string> = {
  unsupported: 'This browser cannot report your location.',
  insecure: 'Location needs a secure (https) connection.',
  denied: 'Location permission denied. Enable it in site settings.',
  unavailable: 'Your location is unavailable right now.',
  timeout: 'Locating timed out. Try again.',
}

/** In the iOS app the switch is the app's, in iOS Settings, not a site's. */
const APP_DENIED = 'Location is off for Fusion WX. Turn it on in iOS Settings.'

export function geoErrorMessage(kind: GeoErrorKind): string {
  return kind === 'denied' && isNativeShell() ? APP_DENIED : MESSAGES[kind]
}

export interface Coords {
  lat: number
  lon: number
}

/**
 * `navigator.geolocation` as a promise.
 *
 * Own implementation rather than MapLibre's `GeolocateControl` so the button
 * can live in the search form on both layouts.
 *
 * Low accuracy on purpose: a forecast grid cell is 2.5 km, so waking the GPS
 * would cost battery and seconds to buy precision nothing here uses. A
 * five-minute-old fix is just as good.
 *
 * In the iOS app the fix is Core Location's, over the bridge (ios-app-plan
 * §5.4), with the same low accuracy and age: the permission the app holds
 * is then the one that decides, and it is the one alerts where I am upgrade
 * to Always.
 */
export function getCurrentPosition(): Promise<Coords> {
  if (isNativeShell()) return nativePosition()
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      reject(new GeoError('insecure', MESSAGES.insecure))
      return
    }
    if (!navigator.geolocation) {
      reject(new GeoError('unsupported', MESSAGES.unsupported))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => reject(new GeoError(kindOf(err), MESSAGES[kindOf(err)])),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 300_000 },
    )
  })
}

async function nativePosition(): Promise<Coords> {
  const fix = await nativeLocation()
  if (!fix) throw new GeoError('unavailable', MESSAGES.unavailable)
  if ('error' in fix) throw new GeoError(fix.error, geoErrorMessage(fix.error))
  return fix
}

function kindOf(err: GeolocationPositionError): GeoErrorKind {
  if (err.code === err.PERMISSION_DENIED) return 'denied'
  if (err.code === err.TIMEOUT) return 'timeout'
  return 'unavailable'
}

/**
 * What the browser will do if asked for a position: `prompt` asks, `granted`
 * answers, `denied` refuses without asking. `unknown` where the Permissions API
 * is missing or will not answer for geolocation, in which case asking is the
 * only way to find out. `insecure` and `unsupported` mean asking is pointless.
 *
 * Never prompts by itself. `navigator.permissions` has been Baseline since
 * Safari 16 (September 2022), so `unknown` is the rare case.
 */
export type GeoPermission = 'granted' | 'prompt' | 'denied' | 'unknown' | 'insecure' | 'unsupported'

export async function geoPermission(): Promise<GeoPermission> {
  if (isNativeShell()) {
    const permission = await nativeLocationPermission()
    if (permission === null) return 'unknown'
    return permission === 'whenInUse' || permission === 'always' ? 'granted' : permission
  }
  if (typeof window !== 'undefined' && !window.isSecureContext) return 'insecure'
  if (!navigator.geolocation) return 'unsupported'
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' })
    const state = status?.state
    return state === 'granted' || state === 'prompt' || state === 'denied' ? state : 'unknown'
  } catch {
    return 'unknown'
  }
}

/**
 * A point at the server's own precision: `f"{lat:.4f},{lon:.4f}"` in
 * `app/services/nws.py`, 11 m. One place is then one URL, for the service
 * worker's forecast cache as much as for the server's caches.
 */
export function canonical(value: number): number {
  return Math.round(value * 1e4) / 1e4
}

const EARTH_RADIUS_KM = 6371.0088

/**
 * Great-circle distance in kilometres. The same haversine `app/api/compare.py`
 * uses for the station distance; at the 1 km this is compared against, the
 * earth's flattening is a rounding error.
 */
export function distanceKm(a: Coords, b: Coords): number {
  const rad = Math.PI / 180
  const dLat = (b.lat - a.lat) * rad
  const dLon = (b.lon - a.lon) * rad
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * Two fixes this close are one place. Under half an NDFD cell (2.5 km) and a
 * third of an HRRR one (3 km), so no forecast here can tell them apart, and
 * treating them as one keeps an unmoved phone on the same URL at every launch.
 */
export const SAME_PLACE_KM = 1
